import { useEffect, useState } from 'react';
import { api } from '../api';
import { useSession } from '../session';
import { baseOptions, useChart } from './charts';
import type { Ctx, Tab } from './Dashboard';
import { TABS } from './Dashboard';
import { LedgerFilter, Line } from './data';
import { ddmmyyyy, lastDay, ml, num, rub, rubx } from './fmt';

interface Row {
  id: string; op_date: string; month: string; amount: number; line: Line; description: string | null; comment: string | null;
  category: string; group: string | null; payment_method: 'cash' | 'bank' | null; legal_entity: string; is_shared: boolean; department: string | null;
}
interface Result { total: number; income: number; expense: number; months: { month: string; income: number | null; expense: number | null }[]; rows: Row[] }

const PAY: Record<string, string> = { cash: 'Наличные', bank: 'Банк' };
const SPECIAL: [Line, string][] = [['owner', 'Выплаты собственнику'], ['refund', 'Возвраты пациентам'], ['unmapped', 'Без статьи — проверить'], ['transfer', 'Перемещения']];
const PER = 50;
type Sort = 'date' | 'amount' | 'description' | 'category' | 'group' | 'pay';

/** Операции: поиск и фильтры, итоги, суммы по месяцам и таблица. Фильтрует и считает БД (dash.ledger). */
export function Ledger({ ctx, initial, back, onBack }: { ctx: Ctx; initial: LedgerFilter; back: Tab | null; onBack: () => void }) {
  const { catalog } = useSession();
  const { series, meta, from, to, le } = ctx;
  const M = series.months;
  const start = (): Required<Pick<LedgerFilter, 'type' | 'from' | 'to'>> & LedgerFilter => ({
    type: 'all', from: M[from], to: M[to], ...initial,
  });
  const [f, setF] = useState(start);
  const [q, setQ] = useState(initial.q ?? '');
  const [sort, setSort] = useState<{ k: Sort; desc: boolean }>({ k: 'date', desc: true });
  const [page, setPage] = useState(0);
  const [res, setRes] = useState<Result | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const set = (patch: Partial<LedgerFilter>) => {
    setF((x) => ({ ...x, day: undefined, ...patch }));
    setPage(0);
  };
  useEffect(() => {
    const h = setTimeout(() => {
      if ((f.q ?? '') !== q) set({ q });
    }, 300);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const params = (offset: number, limit: number) => {
    const p = new URLSearchParams({ from: `${f.from}-01`, to: lastDay(f.to), type: f.type, sort: sort.k, desc: sort.desc ? '1' : '0', offset: String(offset), limit: String(limit) });
    if (le) p.set('le', le);
    for (const k of ['day', 'line', 'group', 'category', 'pay', 'q'] as const) if (f[k]) p.set(k, String(f[k]));
    return p;
  };
  useEffect(() => {
    api.get<Result>(`/dashboard/ledger?${params(page * PER, PER)}`).then(setRes, () => setRes(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f, sort, page, le]);

  const ms = M.filter((m) => m >= f.from && m <= f.to);
  const byM = new Map((res?.months ?? []).map((x) => [x.month.slice(0, 7), x]));
  const chart = useChart((T) => (res ? {
    type: 'bar',
    data: {
      labels: ms.map(ml),
      datasets: [
        { label: 'Приход', data: ms.map((m) => byM.get(m)?.income ?? 0), backgroundColor: T.c[0], maxBarThickness: 18 },
        { label: 'Расход', data: ms.map((m) => byM.get(m)?.expense ?? 0), backgroundColor: T.c[1], maxBarThickness: 18 },
      ],
    },
    options: baseOptions(T, { autoSkip: true }),
  } : null) as never, [res]);

  const pages = Math.max(1, Math.ceil((res?.total ?? 0) / PER));
  const groupValue = f.line && f.line !== 'opex' && f.line !== 'capex' ? `l:${f.line}` : f.group ? `g:${f.group}` : '';
  const sortBy = (k: Sort) => {
    setSort((s) => (s.k === k ? { k, desc: !s.desc } : { k, desc: k === 'date' || k === 'amount' }));
    setPage(0);
  };
  const th = (k: Sort, t: string, n = false) => (
    <th className={`sortable${n ? ' n' : ''}`} onClick={() => sortBy(k)} aria-sort={sort.k === k ? (sort.desc ? 'descending' : 'ascending') : 'none'}>
      {t}{sort.k === k ? (sort.desc ? ' ↓' : ' ↑') : ''}
    </th>
  );
  const copyCsv = async () => {
    const r = await api.get<Result>(`/dashboard/ledger?${params(0, 500)}`);
    const esc = (v: string | null) => `"${(v ?? '').replace(/"/g, '""')}"`;
    const csv = ['Дата;Сумма;Назначение;Статья;Группа;Оплата;ООО']
      .concat(r.rows.map((x) => [x.op_date, String(x.amount).replace('.', ','), esc(x.description), esc(x.category), esc(x.group), PAY[x.payment_method ?? ''] ?? '—', esc(x.legal_entity)].join(';')))
      .join('\n');
    try {
      await navigator.clipboard.writeText(csv);
      setToast(`Скопировано строк: ${r.rows.length}${r.total > r.rows.length ? ` из ${r.total} (первые 500)` : ''}`);
    } catch {
      setToast('Браузер не дал доступ к буферу');
    }
    setTimeout(() => setToast(null), 2200);
  };

  return (
    <>
      {back && back !== 'ledger' && (
        <div className="crumb">
          <button type="button" onClick={onBack}>← {TABS.find(([k]) => k === back)?.[1]}</button>
          <span>· операции открыты из раздела</span>
        </div>
      )}
      <div className="card">
        <div className="filters">
          <label>Поиск<input type="search" placeholder="хеликс, аренда, вывод…" value={q} onChange={(e) => setQ(e.target.value)} /></label>
          <label>Тип
            <select value={f.type} onChange={(e) => set({ type: e.target.value as LedgerFilter['type'] })}>
              <option value="all">все</option><option value="inc">приход</option><option value="exp">расход</option>
            </select>
          </label>
          <label>Группа
            <select value={groupValue} onChange={(e) => {
              const v = e.target.value;
              set({ group: v.startsWith('g:') ? v.slice(2) : undefined, line: v.startsWith('l:') ? (v.slice(2) as Line) : undefined });
            }}>
              <option value="">все группы</option>
              {meta.groups.map((g) => <option key={g.code} value={`g:${g.code}`}>{g.name}</option>)}
              {SPECIAL.map(([l, t]) => <option key={l} value={`l:${l}`}>{t}</option>)}
            </select>
          </label>
          <label>Статья
            <select value={f.category ?? ''} onChange={(e) => set({ category: e.target.value || undefined })}>
              <option value="">все статьи</option>
              {(catalog?.categories ?? []).map((c) => <option key={c.id} value={c.id}>{c.group_name ? `${c.group_name} → ` : ''}{c.name}</option>)}
            </select>
          </label>
          <label>Оплата
            <select value={f.pay ?? ''} onChange={(e) => set({ pay: (e.target.value || undefined) as LedgerFilter['pay'] })}>
              <option value="">любая</option><option value="bank">Банк</option><option value="cash">Наличные</option><option value="none">не указана</option>
            </select>
          </label>
          <label>С
            <select value={f.from} onChange={(e) => set({ from: e.target.value, to: e.target.value > f.to ? e.target.value : f.to })}>
              {M.map((m) => <option key={m} value={m}>{ml(m)}</option>)}
            </select>
          </label>
          <label>По
            <select value={f.to} onChange={(e) => set({ to: e.target.value, from: e.target.value < f.from ? e.target.value : f.from })}>
              {M.map((m) => <option key={m} value={m}>{ml(m)}</option>)}
            </select>
          </label>
          <button className="btn" type="button" onClick={() => {
            setQ('');
            setF({ type: 'all', from: M[from], to: M[to] });
            setPage(0);
          }}>Сбросить</button>
        </div>
      </div>
      <section className="kpis">
        {[
          ['Найдено строк', num(res?.total), f.day ? `за ${ddmmyyyy(f.day)}` : ''],
          ['Приход', rub(res?.income), ''],
          ['Расход', rub(res?.expense), 'без перемещений'],
          ['Сальдо', rub(res ? res.income - res.expense : null), ''],
        ].map(([l, v, d]) => (
          <div className="kpi" key={l}><span className="l">{l}</span><span className="v">{v}</span><span className="d">{d}</span></div>
        ))}
      </section>
      <div className="card">
        <div className="hd">
          <div>
            <h3>Найденные операции по месяцам</h3>
            <p className="sub">{ml(f.from)} — {ml(f.to)}</p>
          </div>
          <button className="btn" type="button" onClick={() => void copyCsv()}>Копировать CSV</button>
        </div>
        <div className="ch" style={{ height: 160 }}>
          <canvas ref={chart} role="img" aria-label="Сумма найденных операций по месяцам" />
        </div>
      </div>
      <div className="card tbl">
        <table>
          <thead>
            <tr>
              {th('date', 'Дата')}
              {th('amount', 'Сумма', true)}
              {th('description', 'Назначение')}
              {th('category', 'Статья')}
              {th('group', 'Группа')}
              {th('pay', 'Оплата')}
              {!le && <th>ООО</th>}
            </tr>
          </thead>
          <tbody>
            {(res?.rows ?? []).map((r) => (
              <tr key={`${r.id}`}>
                <td className="n" style={{ textAlign: 'left' }}>{ddmmyyyy(r.op_date)}</td>
                <td className={`n${r.amount > 0 ? ' pos' : ''}`}>{r.amount > 0 ? '+' : ''}{rubx(r.amount)}</td>
                <td style={{ minWidth: 200 }}>
                  {r.description}
                  {r.comment && <div className="note" style={{ margin: 0 }}>{r.comment}</div>}
                </td>
                <td>
                  {r.category}
                  {r.line === 'unmapped' && <> <span className="pill p-warn">проверить</span></>}
                </td>
                <td style={{ color: 'var(--muted)' }}>{r.line === 'transfer' ? 'перемещение' : r.amount > 0 ? 'приход' : r.group}</td>
                <td>{PAY[r.payment_method ?? ''] ?? '—'}</td>
                {!le && <td>{r.legal_entity}{r.is_shared && <> <span className="pill p-info">делится</span></>}</td>}
              </tr>
            ))}
            {res && !res.rows.length && (
              <tr><td colSpan={7} style={{ color: 'var(--muted)' }}>Ничего не найдено. Измените фильтры или период.</td></tr>
            )}
          </tbody>
        </table>
        <div className="pager">
          <span>стр. {page + 1} из {pages}</span>
          <button className="btn" type="button" disabled={!page} onClick={() => setPage(page - 1)} aria-label="Предыдущая страница">←</button>
          <button className="btn" type="button" disabled={page >= pages - 1} onClick={() => setPage(page + 1)} aria-label="Следующая страница">→</button>
        </div>
      </div>
      {toast && <div className="toast" role="status">{toast}</div>}
    </>
  );
}
